import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import useAuth from "../../../../hooks/useAuth";

import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { useUpdateEntity } from "../../../../hooks/entity";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

const schema = Joi.object({
  entity_name: Joi.string()
    .trim()
    .min(2)
    .max(30)
    .required()
    .label("Service name"),
  entity_description: Joi.string()
    .trim()
    .max(250)
    .optional()
    .allow(null, "")
    .label("Service description"),
}).required();

const UpdateEntity = ({ data }: { data: any }) => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.ENTITY_SETTINGS, Level.WRITE);

  const { mutate, isLoading } = useUpdateEntity();
  const {
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: {
      entity_name: data?.entity_name ?? "",
      entity_description: data?.entity_description ?? "",
    },
  });

  useEffect(() => {
    setValue("entity_name", data?.entity_name);
    setValue("entity_description", data?.entity_description);
  }, [data, setValue]);

  const onSubmit = (data: any) => {
    mutate({
      customer_identification,
      entity_identification: user.entity_identification,
      data: data,
    });
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <fieldset disabled={!canWrite} className="border-0 m-0 p-0 min-w-0">
          <div className="pb-2 flex justify-start items-start flex-wrap md:flex-nowrap md:gap-5 border-b border-solid ">
            <div className="mb-3 w-full md:w-[300px]">
              <CustomInput
                name="entity_name"
                control={control}
                defaultValue={data?.entity_name}
                grayBackground
                error={errors.entity_name}
                helperText={errors.entity_name?.message}
                label={t("dashboard.entities.updateEntity.label-01") as string}
                maxLength={30}
              />
              <CharacterCounter
                currentLength={watch("entity_name")?.length || 0}
                maxLength={30}
              />
            </div>

            <div className="mb-3 w-full md:w-3/4">
              <CustomInput
                name="entity_description"
                control={control}
                grayBackground
                defaultValue={data?.entity_description}
                error={errors.entity_description}
                helperText={errors.entity_description?.message}
                label={t("dashboard.entities.updateEntity.label-02") as string}
                maxLength={200}
              />
              <CharacterCounter
                currentLength={watch("entity_description")?.length || 0}
                maxLength={200}
              />
            </div>
          </div>

          {canWrite && (
            <CustomButton
              type="submit"
              variant="primary"
              className="mt-5 mr-0 ml-auto"
              disabled={
                isLoading ||
                (watch("entity_name") === data?.entity_name &&
                  watch("entity_description") === data?.entity_description)
              }
              isLoading={isLoading}
              title={t("dashboard.entities.updateEntity.btn-01")}
            />
          )}
        </fieldset>
      </form>
    </div>
  );
};

export default UpdateEntity;
